const { urlService } = require('../services/urlService');

const stripTags = string =>
  string ? string.replaceAll(/(<([^>]+)>)/gi, '') : string;

/**
 * Return all attributes from a string containing an html tag.
 *
 * It works case-insensitive with multiline data,
 * and finds any attribute on any format tag.
 *
 * @param {string} stringHtmlTag html tag string to work with
 * @returns {object} Object with all found attributes in the form of key => value
 */
const getHtmlTagAttributes = stringHtmlTag => {
  const regex =
    /[\\s\\r\\t\\n]*([a-z0-9\\-_]+)[\\s\\r\\t\\n]*=[\\s\\r\\t\\n]*(['"])((?:\\\\\\2|(?!\\2).)*)\\2'/gi;
  const attributes = {};
  let match = regex.exec(stringHtmlTag);
  do {
    if (match) {
      attributes[match[1]] = match[3];
    }
    match = regex.exec(stringHtmlTag);
  } while (match);
  return attributes;
};

const getWords = (string, numberOfWords, ellipsis = '...' /* … */) => {
  if (string) {
    const result = stripTags(string)
      .split(' ')
      .splice(0, numberOfWords)
      .join(' ');
    if (result !== string) {
      return `${result} ${ellipsis}`;
    }
  }
  return string;
};

/**
 * Searches for a tag inside a given HTML string.
 *
 * It works case-insensitive with multiline data (not possible using RegExp),
 * and finds any kind of tag on any format: <tag>, <tag/> or <tag>*</tag>.
 * At this moment, this function does not support nested tags for the same tag,
 * e.g.- <tag><tag></tag></tag>.
 *
 * @param {string} string HTML string
 * @param {string} tagName Tag to search
 * @returns {array} Array of found tags
 */
const findTags = (string, tagName, options = {}) => {
  if (!string || string.includes(`<${tagName}`)) {
    return [];
  }

  const openingTagRegExp = new RegExp(`(<${tagName}[^>]*>)`, 'gi');
  const rawTags = string.split(openingTagRegExp);
  // Remove first entry, since it contains the whole matched string.
  rawTags.shift();
  // Join data since every tag is split into two array elements.
  const joinedTags = [];
  rawTags.forEach((rawTag, index) => {
    if (index % 2 !== 0) {
      joinedTags.push({ openingTag: rawTags[index - 1], rest: rawTag });
    }
  });

  const tags = joinedTags.map(tag => {
    // Search for a closing tag.
    const closingTagRegExp = new RegExp(`(</${tagName}>)`, 'i');
    const restParts = tag.rest.split(closingTagRegExp);

    // Closing tag NOT FOUND.
    let tagsData = {
      fullTag: tag.openingTag,
      openingTag: tag.openingTag,
      closingTag: undefined,
      innerHtml: undefined
    };

    // Closing tag FOUND.
    if (restParts[1]) {
      tagsData = {
        fullTag: `${tag.openingTag}${restParts[0]}${restParts[1]}`,
        openingTag: tag.openingTag,
        closingTag: restParts[1],
        innerHtml: restParts[0]
      };
    }

    if (options.attributes) {
      tagsData.attributes = getHtmlTagAttributes(tagsData.fullTag);
    }

    return tagsData;
  });

  return tags;
};

const getExternalLinks = html => {
  const links = findTags(html, 'a', { attributes: true });
  const externalLinks = [];
  links.forEach(link => {
    if (link.attributes.href && urlService.isExternal(link.attributes.href)) {
      externalLinks.push(link);
    }
  });
  return externalLinks;
};

const addAttributeToTag = (tag, attribute, values) => {
  // We already have values in this attribute
  const updatedTag = tag;
  if (tag.attributes[attribute]) {
    updatedTag.attributes[attribute] = [
      ...new Set(values.concat(tag.attributes[attribute].split(' ')))
    ].join(' ');
    const regularExpression = new RegExp(`${attribute}=["'](.*?)["']`);
    // const regularExpression = /rel=["'](.*?)["']/;
    updatedTag.fullTag = updatedTag.fullTag.replace(
      regularExpression,
      `${attribute}="${updatedTag.attributes[attribute]}"`
    );
    updatedTag.openingTag = updatedTag.openingTag.replace(
      regularExpression,
      `${attribute}="${updatedTag.attributes[attribute]}"`
    );
  } else {
    const valuesString = values.join(' ');
    const tagParts = tag.fullTag.split(' ');
    const regularExpression = new RegExp(`${tagParts[0]} `);
    updatedTag.fullTag = updatedTag.fullTag.replace(
      regularExpression,
      `${tagParts[0]} ${attribute}="${valuesString}" `
    );
    updatedTag.openingTag = updatedTag.openingTag.replace(
      regularExpression,
      `${tagParts[0]} ${attribute}="${valuesString}" `
    );
    updatedTag.attributes[attribute] = valuesString;
  }
  return updatedTag;
};

const truncateText = (string, maxChars, ellipsis = '...' /* … */) => {
  if (!string) {
    return string;
  }
  let textLength = 0;
  const originalWords = stripTags(string).split(' ');
  const truncatedWords = originalWords.filter(word => {
    textLength += word.length + 1; // 1 is for white space between words
    return textLength <= maxChars;
  });

  return originalWords.length === truncatedWords.length
    ? originalWords.join(' ')
    : `${truncatedWords.join(' ')}${ellipsis}`;
};

const isEmail = email => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

const ucFirst = string => string.charAt(0).toUpperCase() + string.slice(1);

const zeroFill = (string, number) =>
  `${'0'.repeat(number)}${string}`.slice(-number);

const toUnicode = string => {
  if (string) {
    return [...string]
      .map(char => {
        // Only decodes "special chars" not a-z (97 to 122) OR A-Z (65 to 90)
        if (
          char.codePointAt(0) === 44 ||
          char.codePointAt(0) === 45 ||
          char.codePointAt(0) === 46 ||
          char.codePointAt(0) === 32 ||
          (char.codePointAt(0) >= 48 && char.codePointAt(0) <= 57) ||
          (char.codePointAt(0) >= 65 && char.codePointAt(0) <= 90) ||
          (char.codePointAt(0) >= 97 && char.codePointAt(0) <= 122)
        ) {
          return char;
        }
        let unicode = char.codePointAt(0).toString(16).toUpperCase();
        while (unicode.length < 4) {
          unicode = `0${unicode}`;
        }
        unicode = `\\u${unicode}`;
        return unicode;
      })
      .reduce((previous, current) => previous + current);
  }
  return null;
};

const htmlEntities = {
  nbsp: ' ',
  cent: '¢',
  pound: '£',
  yen: '¥',
  euro: '€',
  copy: '©',
  reg: '®',
  lt: '<',
  gt: '>',
  quot: '"',
  amp: '&',
  apos: "'"
};

const removeHtmlEntities = string => {
  return string.replaceAll(/&([^;]+);/g, (entity, entityCode) => {
    if (entityCode in htmlEntities) {
      return htmlEntities[entityCode];
    }
    if (/^#x([\da-fA-F]+)$/.test(entityCode)) {
      const match = entityCode.match(/^#x([\da-fA-F]+)$/);
      return String.fromCodePoint(Number.parseInt(match[1], 16));
    }
    if (/^#(\d+)$/.test(entityCode)) {
      const match = entityCode.match(/^#(\d+)$/);
      return String.fromCodePoint(match[1]);
    }
    return entity;
  });
};

const removeHtml = string =>
  string ? removeHtmlEntities(stripTags(string)) : string;

const normalizeDiacritics = str =>
  str.normalize('NFD').replaceAll(/[\u0300-\u036F]/g, '');

const slugify = str =>
  normalizeDiacritics(str)
    .toLowerCase()
    .trim()
    .replaceAll(/\s+/g, '-')
    .replaceAll(/[^\w-]+/g, '')
    .replaceAll(/--+/g, '-');

const processLink = match => {
  const normalizedLink = match
    .replaceAll(/"/g, '')
    .replace(' href=', '')
    .replace(/:\/\/[^0-z]+/, '://')
    .replaceAll(/(^[\s\u200B]*|[\s\u200B]*$)/g, '') // see https://stackoverflow.com/questions/24205193/javascript-remove-zero-width-space-unicode-8203-from-string
    .trim();
  return ` href="${normalizedLink}"`;
};

const removeSpacesFromHtmlHref = html => {
  return html.replaceAll(/ href="[^"]*"/g, processLink);
};

/**
 * Tells whether a path is JSON or not.
 *
 * @remarks
 * A fast checker that avoids reading the contents of a file, or doing
 * some other complex operation to determine if a file contains JSON data.
 */
const isJsonFile = filepath => filepath.slice(-5) === '.json';

const getCase = string =>
  string === string.toUpperCase() ? 'uppercase' : 'lowercase';

module.exports = {
  stripTags,
  findTags,
  getHtmlTagAttributes,
  getExternalLinks,
  addAttributeToTag,
  getWords,
  truncateText,
  isEmail,
  ucFirst,
  zeroFill,
  toUnicode,
  removeHtmlEntities,
  removeHtml,
  slugify,
  normalizeDiacritics,
  removeSpacesFromHtmlHref,
  isJsonFile,
  getCase
};
